import React, { useEffect, useState } from 'react'
import { Card, Box, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import BoxWithBorder from 'components/atoms/container/BoxWithBorder'
import OverlayLoading from 'components/atoms/OverlayLoading'
import { NotificationBodyText } from 'components/atoms/Text'
import CompletionCard from 'components/molecules/journey/CompletionCard'
import JourneyPath from 'components/molecules/journey/JourneyPath'
import StepContentsCard from 'components/molecules/journey/StepContentsCard'
import CommonDialog from 'components/molecules/notificationOverlay/CommonDialog'
import { useNavigate } from 'react-router-dom'
import { USER_SETTINGS } from 'routes/constants'
import { updateStates, updateUserCurrentGoalStep } from 'services/userServices'
import { checkSaved } from 'utils/contentEditorHelper'
import { useTranslation } from 'react-i18next'
import IconButton from '@mui/material/IconButton'
import MenuOpenOutlinedIcon from '@mui/icons-material/MenuOpenOutlined'
import WatchLaterOutlinedIcon from '@mui/icons-material/WatchLaterOutlined'
import TourOutlinedIcon from '@mui/icons-material/TourOutlined'
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined'
import { NARROW_BROWSER_WIDTH } from 'utils/constants'
import { MilestoneEncouragementPopup } from 'components/organisms/JourneyPopups'
import { trackPathAction } from 'services/amplitudeTracking'
import { getGoalInfoById, getGoalsProgressById } from 'utils/getSupportingInfo'
import InCardStack from 'components/atoms/container/InCardStack'
import RowStack from 'components/atoms/container/RowStack'
import { formatSeconds } from 'utils/formatDateTime'
import IconTextGroup from 'components/atoms/container/IconTextGroup'
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined'
import colors from 'styles/colors'
import { compileGoalSteps } from 'services/goalServices'

const JourneyContents = ({
  steps,
  goalId,
  stepId,
  userStates,
  editorMode = false,
  demoMode = false
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [scheduleReminderOpen, setScheduleReminderOpen] = useState(false)
  const [slowdownReminderOpen, setSlowDownReminderOpen] = useState(false)
  const [organizedSteps, setOrganizedSteps] = useState([])
  const [processedSteps, setProcessedSteps] = useState([])
  const [currentStep2, setCurrentStep2] = useState({})
  const [currentL1StepNumber, setCurrentL1StepNumber] = useState(0)
  const [currentStepNumber, setCurrentStepNumber] = useState(0)
  const [contentStartIndex, setContentStartIndex] = useState(0)
  const [showCompletionCard, setShowCompletionCard] = useState(false)
  const [completeGoalNow, setCompleteGoalNow] = useState(false)
  const [initialized, setInitialized] = useState(false)
  const [loading, setLoading] = useState(false)
  const [pathOpen, setPathOpen] = useState(
    window.innerWidth > NARROW_BROWSER_WIDTH
  )
  const [encouragementMessage, setEncouragementMessage] = useState()
  const [isCurrentGoal, setIsCurrentGoal] = useState(false)
  const [goal, setGoal] = useState()
  const [goalProgress, setGoalProgress] = useState()
  const [goalLengthSeconds, setGoalLengthSeconds] = useState(0)
  const [isRolePlayStep, setIsRolePlayStep] = useState(false)

  const findStepNumber = (currentGoalStepId, l2Steps, resultSteps) => {
    const targetStep = steps.find(s => s.id === parseInt(currentGoalStepId))
    let completedGoalStepNumber
    if (targetStep?.number) {
      const activeStep = l2Steps.find(s => s.number > targetStep.number)
      completedGoalStepNumber = activeStep?.number || targetStep.number
      setCurrentL1StepNumber(
        activeStep?.l1StepNumber || targetStep.l1StepNumber
      )
    } else {
      completedGoalStepNumber = resultSteps[0].childL2Steps[0].number
      setCurrentL1StepNumber(resultSteps[0].childL2Steps[0].l1StepNumber)
    }

    return completedGoalStepNumber
  }

  useEffect(() => {
    const fetchData = async () => {
      // Check user stats for journey process
      let completedGoalStepNumber
      let isUsersCurrentGoal = userStates.currentGoalId === parseInt(goalId)

      // Get Goal progress if not current goal
      const goalProgress = await getGoalsProgressById(goalId)
      setGoalProgress(goalProgress ?? {})
      const { l2Steps, resultSteps, stats } = compileGoalSteps(
        steps,
        goalProgress?.currentGoalStepId
      )
      setGoalLengthSeconds(stats?.goalLengthSeconds)
      setOrganizedSteps(resultSteps)
      const maxStepNumber = steps[steps.length - 1].number
      completedGoalStepNumber = findStepNumber(
        goalProgress?.currentGoalStepId,
        l2Steps,
        resultSteps
      )

      if (
        userStates.completedGoals?.indexOf(parseInt(goalId)) > -1 ||
        editorMode ||
        demoMode
      ) {
        // if user already completed the goal
        completedGoalStepNumber = maxStepNumber
      }
      setCurrentStepNumber(completedGoalStepNumber)
      setIsCurrentGoal(isUsersCurrentGoal)

      // if provide stepId in page state, set current content
      if (stepId) {
        const targetStep = steps.find(s => s.id === parseInt(stepId))
        if (targetStep) {
          if (targetStep.milestone_level === 2) setCurrentStep2(targetStep)
          else if (targetStep.milestone_level === 1) {
            const selectedL2Step = l2Steps.find(
              s => s.number > targetStep.number
            )
            setCurrentStep2(selectedL2Step)
          } else if (targetStep.milestone_level === 3) {
            const selectedL2Step = l2Steps
              .sort((a, b) => b.number - a.number)
              .find(s => s.number < targetStep.number)
            setCurrentStep2(selectedL2Step)
            const childStep = selectedL2Step.childL3Steps.find(
              c => c.id === stepId
            )
            const childIndex = selectedL2Step.childL3Steps.indexOf(childStep)
            setContentStartIndex(childIndex)
          }
        }
      } else if (completedGoalStepNumber === maxStepNumber) {
        // If finished goal, goes to first step.
        setCurrentStep2(l2Steps[0])
      } else {
        // Set current content as completedGoalStepNumber
        const targetStep = l2Steps.find(
          s => s.number === completedGoalStepNumber
        )
        if (targetStep) setCurrentStep2(targetStep)
      }
      setProcessedSteps(steps)
      setInitialized(true)
    }

    if (steps && userStates && !initialized) {
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps])

  const pathClickHandler = (l1Index, l2Index) => {
    if (editorMode && !checkSaved()) {
      return
    }
    setCurrentStep2(organizedSteps[l1Index].childL2Steps[l2Index])
    setContentStartIndex(0)
    setShowCompletionCard(false)
  }

  const handlePreviousStep2 = () => {
    const previousL2Step = processedSteps
      .sort((a, b) => b.number - a.number)
      .find(s => s.number < currentStep2.number && s.milestone_level === 2)
    if (previousL2Step) {
      setCurrentStep2(previousL2Step)
      setContentStartIndex(-1)
    }
  }

  const updateUserCurrentStepId = async newStepId => {
    let res
    if (isCurrentGoal) {
      res = await updateStates({ currentGoalStepId: newStepId }, false)
      userStates.currentGoalStepId = newStepId
    } else if (goalProgress) {
      res = await updateUserCurrentGoalStep({ goalStepId: newStepId })
      goalProgress.currentGoalStepId = newStepId
      setGoalProgress(goalProgress)
    }
    if (res?.data?.data?.firstGoalStepCompleted) {
      setScheduleReminderOpen(true)
    }
    if (res?.data?.data?.displayLearningSlowdown) {
      setSlowDownReminderOpen(true)
    }
  }

  const updateUserGoalCompletion = async completed => {
    const completedGoals = completed || []
    if (completedGoals.indexOf(goalId) < 0) {
      trackPathAction(goalId, 'Path Completed')
      completedGoals.push(goalId)
      const requestBody = {
        completedGoals
      }
      await updateStates(requestBody)
      setCompleteGoalNow(true)
    }
    setLoading(false)
  }

  const showEncouragementPopupIfEligible = currentStep => {
    const nextMilestoneIndex = organizedSteps.findIndex(
      x => x.number === currentStep.l1StepNumber
    )
    const nextMilestoneName = organizedSteps[nextMilestoneIndex]?.name
    const isActivePath = currentStep.l1StepNumber >= currentL1StepNumber
    if (currentStep.encouragementMessage && isActivePath) {
      setEncouragementMessage({
        description: currentStep.encouragementMessage.description,
        scores: currentStep.encouragementMessage.scores,
        nextMilestoneName
      })
    }
  }

  // handle user profile update on currentGoalStepId
  const handleNextStep2 = (isNavigation = true, isStepUpdate = true) => {
    // Only move steps if current goal not completed and not editor mode
    const notCompleteGoal =
      !userStates.completedGoals ||
      userStates.completedGoals.indexOf(parseInt(goalId)) === -1
    let updatedUserCurrentStepId = false
    if (notCompleteGoal && !editorMode && isStepUpdate) {
      const currentStep2Id = goalProgress?.currentGoalStepId
      const currentUserStep = processedSteps.find(s => s.id === currentStep2Id)
      if (!currentStep2Id || currentUserStep.number < currentStep2.number) {
        updateUserCurrentStepId(currentStep2.id)
        updatedUserCurrentStepId = true
        const currentL1Step = organizedSteps.find(
          s => s.number === currentL1StepNumber
        )
        if (currentStep2.isFristL2ForL1) {
          trackPathAction(goalId, 'Milestone Started', {
            milestone: currentL1Step.name
          })
          if (currentStep2.isFirstL2ForGoal) {
            trackPathAction(goalId, 'Path Started')
          }
        }
        if (currentStep2.isLastL2Step) {
          trackPathAction(goalId, 'Milestone Completed', {
            milestone: currentL1Step.name
          })
        }
      }
    }
    const nextL2Step = processedSteps
      .sort((a, b) => a.number - b.number)
      .find(s => s.number > currentStep2.number && s.milestone_level === 2)
    if (nextL2Step && isNavigation) {
      setCurrentStep2(nextL2Step)
      showEncouragementPopupIfEligible(nextL2Step)
      setContentStartIndex(0)
      if (nextL2Step.number > currentStepNumber) {
        setCurrentStepNumber(nextL2Step.number)
        setCurrentL1StepNumber(nextL2Step.l1StepNumber)
      }
    } else if (!nextL2Step) {
      if (isNavigation) {
        setLoading(true)
        setShowCompletionCard(true)
        setCurrentStepNumber(organizedSteps[organizedSteps.length - 1].number)
        setCurrentL1StepNumber(
          organizedSteps[organizedSteps.length - 1].l1StepNumber
        )
        setCurrentStep2({})
      }
      updateUserGoalCompletion(userStates.completedGoals)
    }
    return updatedUserCurrentStepId
  }

  useEffect(() => {
    const handleResize = () => {
      if (pathOpen && window.innerWidth < NARROW_BROWSER_WIDTH) {
        togglePathOpen()
      } else if (!pathOpen && window.innerWidth > NARROW_BROWSER_WIDTH) {
        togglePathOpen()
      }
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.innerWidth])

  const togglePathOpen = () => {
    setPathOpen(!pathOpen)
  }

  useEffect(() => {
    const fetchData = async () => {
      const goalInfo = await getGoalInfoById(parseInt(goalId))
      setGoal(goalInfo)
    }
    if (goalId) {
      fetchData()
    }
  }, [goalId])

  return (
    <Grid
      container
      width='100%'
      sx={{ flexWrap: 'nowrap' }}
      style={{ marginTop: 12 }}>
      <OverlayLoading open={loading} />
      {encouragementMessage ? (
        <MilestoneEncouragementPopup
          open={!!encouragementMessage}
          message={encouragementMessage.description}
          virtueScores={encouragementMessage.scores}
          nextMilestoneName={encouragementMessage.nextMilestoneName}
          onClose={() => setEncouragementMessage(null)}
        />
      ) : null}
      <Grid
        size={pathOpen ? 3 : 2}
        minWidth={pathOpen ? '360px' : '250px'}
        height='inherit'
        data-testid='journey-path-view'>
        {pathOpen ? (
          <InCardStack spacing={6}>
            <InCardStack spacing={6} paddingBottom={4} width={'100%'}>
              <RowStack justifyContent={'space-between'}>
                <Typography variant='h3'>{goal?.name}</Typography>
                <IconButton onClick={togglePathOpen}>
                  <MenuOpenOutlinedIcon />
                </IconButton>
              </RowStack>

              <IconTextGroup
                group={[
                  {
                    icon: <WatchLaterOutlinedIcon />,
                    text: formatSeconds(goalLengthSeconds)
                  },
                  {
                    icon: <TourOutlinedIcon />,
                    text: `${organizedSteps?.length - 1} ${t('pathsAndModels.milestones')}`
                  },
                  {
                    icon: <StickyNote2OutlinedIcon />,
                    text: `${steps?.filter(s => s.milestone_level === 2)?.length} ${t('pathsAndModels.steps')}`
                  }
                ]}
                color='primary'
              />
            </InCardStack>

            <JourneyPath
              organizedSteps={organizedSteps}
              currentStepNumber={currentStepNumber}
              clickHandler={pathClickHandler}
              activeStepNumber={currentStep2.number}
              activeL1StepNumber={currentStep2.l1StepNumber}
              goalId={goalId}
            />
          </InCardStack>
        ) : (
          <BoxWithBorder
            onClick={togglePathOpen}
            sx={{
              width: 250,
              padding: '12px 16px',
              borderRadius: 4,
              borderWidth: 1,
              borderColor: colors.Grey100,
              boxShadow: `0px 1px 4px 0px ${colors.Grey100}`,
              cursor: 'pointer'
            }}>
            <RowStack
              justifyContent={'space-between'}
              alignItems={'flex-start'}>
              <RowStack spacing={4} alignItems={'flex-start'}>
                <RouteOutlinedIcon
                  sx={{
                    color: colors.Grey400,
                    transform: 'rotate(90deg) scaleX(-1)'
                  }}
                />
                <Typography variant='body1Small' fontWeight={600} width={110}>
                  {goal?.name}
                </Typography>
              </RowStack>

              <IconButton>
                <MenuOpenOutlinedIcon sx={{ transform: 'rotate(180deg)' }} />
              </IconButton>
            </RowStack>
          </BoxWithBorder>
        )}
      </Grid>

      <Grid
        size={pathOpen ? 9 : 10}
        paddingLeft={10}
        sx={{ display: 'flex', justifyContent: 'center' }}>
        <Card
          data-testid='path-content-card'
          variant='outlined'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            border: 'none',
            height: 'calc(100vh - 115px)',
            width: '100%',
            overflowY: 'auto',
            position: 'relative',
            '&::-webkit-scrollbar': {
              display: 'none'
            }
          }}>
          <Box
            sx={{
              width: isRolePlayStep ? '100%' : pathOpen ? 700 : 740,
              margin: '0 auto'
            }}>
            {showCompletionCard ? (
              <CompletionCard
                goalId={goalId}
                showReAssessmentInfo={completeGoalNow}
                pathOpen={pathOpen}
              />
            ) : (
              <StepContentsCard
                organizedSteps={organizedSteps}
                l2StepData={currentStep2}
                handleNextStep2={handleNextStep2}
                handlePreviousStep2={handlePreviousStep2}
                firstL2Step={
                  !Boolean(
                    processedSteps?.find(
                      s =>
                        currentStep2.number > s.number &&
                        s.milestone_level === 2
                    )
                  )
                }
                contentStartIndex={contentStartIndex}
                editorMode={editorMode}
                pathOpen={pathOpen}
                isRolePlayStep={isRolePlayStep}
                setIsRolePlayStep={setIsRolePlayStep}
              />
            )}
          </Box>
        </Card>
      </Grid>
      <CommonDialog
        titleText={t('pathsAndModels.scheduleTimeLearn')}
        open={scheduleReminderOpen}
        onClose={confirmed => {
          setScheduleReminderOpen(false)
          if (confirmed) {
            navigate(USER_SETTINGS, { state: { fromJourney: true } })
          }
        }}
        confirmText={t('pathsAndModels.customize')}
        cancelText={t('common.noThanks')}>
        <NotificationBodyText textAlign='center'>
          {t('pathsAndModels.spendingTimeLearning')}
        </NotificationBodyText>
        <NotificationBodyText textAlign='center'>
          {t('pathsAndModels.useTheSettings')}
        </NotificationBodyText>
      </CommonDialog>
      <CommonDialog
        open={slowdownReminderOpen}
        onClose={() => setSlowDownReminderOpen(false)}
        titleText={t('pathsAndModels.coveredLotContent')}
        confirmText={t('common.ok')}>
        <NotificationBodyText textAlign='center'>
          {t('pathsAndModels.movingThroughContentQickly')}
        </NotificationBodyText>
      </CommonDialog>
    </Grid>
  )
}

export default JourneyContents
